import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
	createThreadStream,
	createMessageStream,
	getThread as apiGetThread,
	getThreadsList as apiGetThreadsList,
	deleteThread as apiDeleteThread,
	cancelThreadStream as apiCancelThreadStream,
	cancelThreadStreamRequest as apiCancelThreadStreamRequest
} from '@/api/endpoints/threads'
import { transcribeAudio } from '@/api/endpoints/transcription'
import { parseSSE } from '@/api/sse'
import { classifyLimit, normalizeApiError, ERROR_CODES } from '@/api/errors'
import type {
	ThreadObject,
	MessageObject,
	CreateThreadData,
	CreateMessageDataWithModel,
	transcribeAudioData,
	TopicType
} from '@/types'
import { useCommonStore } from '@/stores/common'
import { useComposerStore } from '@/stores/composer'
import { useUsageStore } from '@/stores/usage'

declare const window: Window

export const useThreadStore = defineStore('thread', () => {
	const thread = ref<ThreadObject>({ messages: [] } as ThreadObject)
	const threads = ref<ThreadObject[]>([])
	const messageStream = ref('')
	const reasoningMessageStream = ref('')
	const errorMessage = ref('')
	const errorShowRetry = ref(true)
	const isStreaming = ref(false)
	const topicName = ref('')
	const userInteracted = ref(false)
	const requestId = ref<number | null>(null)
	const idMockUserMessage = ref<number | null>(null)
	const shouldStopStreaming = ref(false)
	const fetchThread = ref(false)
	const prevThreadId = ref<number | null>(null)
	const awaitMessage = ref(false)
	const deleteThreadArr = ref<number[]>([])
	// Флаги режима треда (выставляются из типа топика)
	const currentTopicType = ref<TopicType | null>(null)
	const secretMode = ref(false)
	// Сигнал "проскроллить вниз" вместо прямого window.scrollTo из стора (скроллит ThreadView)
	const requestScrollBottom = ref(0)
	// Внутренний счётчик активных withLoading: гасим loading/sendingMessage только когда
	// завершилась последняя обёрнутая операция (без раннего return, который молча ронял вызов).
	let fetchCounter = 0
	// Прислал ли сервер срез usage в текущем стриме. Если нет — после стрима добираем
	// его запросом, иначе бар остатков врал бы до перезагрузки страницы.
	let usageFromStream = false

	const threadName = computed(() => {
		if (imgMode.value) return 'Генерация изображения'
		return thread.value?.name || ''
	})
	const imgMode = computed(() => currentTopicType.value === 'image')
	const imageEditMode = computed(() => currentTopicType.value === 'image_edit')
	const fileAvailable = computed(() => currentTopicType.value === 'image_edit' || currentTopicType.value === 'document')

	function setTopicType(type: TopicType | null) {
		currentTopicType.value = type
	}

	async function withLoading<T>(action: () => Promise<T>, errorMessage?: string): Promise<T | undefined> {
		const commonStore = useCommonStore()
		fetchCounter++
		commonStore.enableLoading()
		try {
			return await action()
		} catch (error) {
			console.error(errorMessage || 'An error occurred:', error)
			throw error
		} finally {
			fetchCounter = Math.max(0, fetchCounter - 1)
			if (fetchCounter === 0) {
				commonStore.disableLoading()
				commonStore.disableSendingMessage()
			}
		}
	}

	function handleStreamEvent(data: any) {
		// Основной канал остатков: сервер добавляет срез в конец стрима (§5.2.1).
		// Передаём событие целиком: plan может лежать рядом с usage, форму разбирает стор.
		// Флаг ставим по результату — нераспознанный срез должен оставить фолбэк-запрос.
		if (data.usage) {
			usageFromStream = useUsageStore().applyUsage(data)
		}
		if (data.id) {
			requestId.value = null
			thread.value = data
		}
		if (data.userMessage && idMockUserMessage.value) {
			thread.value.messages[idMockUserMessage.value - 1] = data.userMessage
			idMockUserMessage.value = null
		}
		if (data.aiMessage) {
			thread.value.messages[thread.value.messages.length - 1] = data.aiMessage
		}
		if (data.chunk) {
			if (data.is_reasoning) {
				reasoningMessageStream.value += data.chunk
			} else {
				messageStream.value += data.chunk
			}
			thread.value.messages[thread.value.messages.length - 1].body = messageStream.value
			thread.value.messages[thread.value.messages.length - 1].reasoning_body = reasoningMessageStream.value
			requestId.value = data.request_id
		}
	}

	async function processStream(reader: ReadableStreamDefaultReader) {
		await parseSSE(reader, {
			onData: (data) => handleStreamEvent(data),
			shouldStop: () => shouldStopStreaming.value
		})
	}

	async function createStreaming(reader: ReadableStreamDefaultReader, sendFile: boolean) {
		isStreaming.value = true
		shouldStopStreaming.value = false
		awaitMessage.value = false
		createStreamMessage(sendFile)
		usageFromStream = false
		try {
			await processStream(reader)
			// Списываем только успешно дочитанный стрим (не в finally: на ошибке запрос не тратится).
			// Для гостя это уменьшает его 3 бесплатных; при серверных лимитах — no-op.
			const usage = useUsageStore()
			usage.consumeText()
			// Фолбэк: бэк может ещё не присылать срез в стриме — тогда спрашиваем сами,
			// иначе бар остатков врал бы до перезагрузки страницы.
			if (!usageFromStream) usage.fetchUsage()
		} finally {
			stopStreaming()
		}
	}

	function stopStreaming() {
		messageStream.value = ''
		reasoningMessageStream.value = ''
		// Раньше здесь был window.scrollTo. Теперь стор только сигналит, а скроллит ThreadView.
		const wantScroll = !userInteracted.value
		isStreaming.value = false
		userInteracted.value = false
		fetchThread.value = false
		if (wantScroll) requestScrollBottom.value++
	}

	function createMessageTemplate(isAI: boolean, sendFile: boolean, content = '', imageUrl = ''): MessageObject {
		const composer = useComposerStore()
		const currentFile = composer.currentFile
		const isImageEditUserFile =
			!isAI && sendFile && imageEditMode.value && !!currentFile.document && currentFile.type.startsWith('image/')
		return {
			body: content,
			created_at: new Date().toISOString(),
			id: 9999,
			is_ai_model: isAI,
			image: isImageEditUserFile && imageUrl ? { url: imageUrl, hash: '' } : null,
			file:
				currentFile.size > 0 && sendFile && !isImageEditUserFile
					? { name: currentFile.name, size_bytes: currentFile.size, mime_type: currentFile.type }
					: null,
			music_tracks: null
		}
	}

	// Сообщение до сервера не доехало — убираем оптимистичный пузырь. Индекс обнуляем
	// обязательно: иначе следующий стрим запишет ответ сервера в чужое сообщение
	// (см. handleStreamEvent → messages[idMockUserMessage - 1]).
	function rollbackMockUserMessage() {
		if (!idMockUserMessage.value) return
		thread.value.messages.splice(idMockUserMessage.value - 1, 1)
		idMockUserMessage.value = null
	}

	// Отправка не состоялась: кроме отката сообщения гасим флаги ожидания. Они снимаются
	// только в createStreaming/clearProccess, а из-за ошибки туда управление не доходит —
	// в результате при возврате в тред включался вечный лоадер (ThreadView следит за awaitMessage).
	function abortPendingSend() {
		rollbackMockUserMessage()
		clearAwaitMessage()
		clearFetchThread()
	}

	function createMockMessage(message: string) {
		const composer = useComposerStore()
		const doc = composer.currentFile.document
		const isImageEditUserFile = imageEditMode.value && !!doc && composer.currentFile.type.startsWith('image/')
		const imageUrl = isImageEditUserFile && doc ? URL.createObjectURL(doc) : ''
		const newMessage = createMessageTemplate(false, true, message, imageUrl)
		idMockUserMessage.value = thread.value.messages.push(newMessage)
	}

	// Возврат на тред с незавершённой генерацией: getThread отдаёт серверное состояние без
	// ещё не сохранённого промпта → показываем оптимистичное сообщение пользователя из задачи.
	function ensureUserPrompt(text: string) {
		// Сравниваем нормализованно: сервер может сохранить промпт с иным trim'ом — иначе получим дубль
		if (thread.value.messages.some((m) => !m.is_ai_model && m.body?.trim() === text.trim())) return
		thread.value.messages.push(createMessageTemplate(false, false, text))
	}

	function createCancelMessage() {
		if (messageStream.value) {
			const newMessage = createMessageTemplate(true, true, messageStream.value)
			messageStream.value = ''
			reasoningMessageStream.value = ''
			thread.value.messages.push(newMessage)
		}
	}

	function createStreamMessage(sendFile: boolean) {
		const newMessage = createMessageTemplate(true, sendFile)
		thread.value.messages.push(newMessage)
	}

	async function createThread(data: CreateThreadData) {
		const composer = useComposerStore()
		return withLoading(async () => {
			setFetchThread()
			if (thread.value.id) clearThread()
			try {
				const reader = await createThreadStream({
					...data,
					model_name: composer.getModelName(data.model_name, true),
					is_secret: secretMode.value,
					is_reasoning: composer.reasoning
				})
				if (reader) await createStreaming(reader, false)
			} catch (e) {
				const error = normalizeApiError(e)
				if (!classifyLimit(error)) throw error
				// Лимит: модалку уже открыл хук интерцептора, здесь только чистим состояние.
				abortPendingSend()
			}
		}, 'Failed to create thread')
	}

	async function createMessage(message: string) {
		const composer = useComposerStore()
		awaitMessage.value = true
		return withLoading(async () => {
			if (!thread.value.id) return
			const payload: CreateMessageDataWithModel = {
				thread_id: thread.value.id,
				promt: message,
				prompt: message,
				model_name: composer.getModelName(thread.value.model),
				is_reasoning: composer.reasoning,
				...(composer.currentFile.document && { document: composer.currentFile.document })
			}
			composer.clearCurrentFile()
			clearErrorMessage()
			try {
				const reader = await createMessageStream(payload)
				if (reader) await createStreaming(reader, true)
			} catch (e) {
				const error = normalizeApiError(e)
				abortPendingSend()
				// Лимит: модалку уже открыл хук интерцептора, ошибку в чате не дублируем.
				if (classifyLimit(error)) return
				if (error.code === ERROR_CODES.UNSUPPORTED_DOCUMENT) {
					setErrorMessage('Некорректный формат файл')
				}
				console.error(error.message)
			}
		}, 'Failed to create message')
	}

	// Применить серверное состояние треда к стору (одна перерисовка списка сообщений).
	function applyThread(data: ThreadObject) {
		thread.value = data
		secretMode.value = !!data.is_secret
		setTopicType(data.topic?.type ?? null)
	}

	async function getThread(id: number) {
		return withLoading(async () => {
			const response = await apiGetThread(id)
			applyThread(response.data)
		}, 'Failed to get thread')
	}

	// Тихое получение треда: без withLoading (глобальный loading → затемнение контента) и без
	// мутации стора. Для опросов «результат уже на сервере?», которые не должны мигать интерфейсом.
	async function fetchThreadSilent(id: number): Promise<ThreadObject> {
		const response = await apiGetThread(id)
		return response.data
	}

	async function getThreadsList() {
		const response = await apiGetThreadsList()
		return response.data
	}

	async function deleteThread(id: number) {
		return withLoading(async () => {
			return await apiDeleteThread(id)
		}, 'Failed to delete thread')
	}

	async function sendAudio(data: transcribeAudioData) {
		if (window.location.pathname.includes('thread')) {
			if (thread.value?.id) {
				data.thread_id = thread.value.id
			}
			if (thread.value?.topic?.id) {
				data.topic_id = thread.value.topic.id
			}
		}
		return withLoading(async () => {
			return await transcribeAudio(data)
		}, 'Failed to transcribe audio')
	}

	async function cancelThreadStream() {
		isStreaming.value = false
		if (requestId.value) {
			await apiCancelThreadStream({ request_id: requestId.value })
		}
	}

	function cancelThreadStreamRequest() {
		apiCancelThreadStreamRequest()
	}

	function addToDeleteThreadArr(id: number) {
		deleteThreadArr.value.push(id)
	}
	function setUserInteracted() {
		userInteracted.value = true
	}
	function setShouldStopStreaming() {
		shouldStopStreaming.value = true
	}
	function setTopicName(name: string) {
		topicName.value = name
	}

	function setErrorMessage(message: string, showRetry: boolean = true) {
		errorMessage.value = message
		errorShowRetry.value = showRetry
	}
	function setFetchThread() {
		fetchThread.value = true
	}
	function toggleSecretChat() {
		secretMode.value = !secretMode.value
	}
	function setSecretMode() {
		secretMode.value = true
	}
	function setPrevThreadId(id: number) {
		prevThreadId.value = id
	}
	function clearPevThreadId() {
		prevThreadId.value = null
	}
	function clearSecretMode() {
		secretMode.value = false
	}
	function clearErrorMessage() {
		errorMessage.value = ''
		errorShowRetry.value = true
	}
	function clearTopicName() {
		topicName.value = ''
	}

	function clearFetchThread() {
		fetchThread.value = false
	}
	function clearUserInteracted() {
		userInteracted.value = false
	}
	function clearAwaitMessage() {
		awaitMessage.value = false
	}
	function clearThread() {
		const composer = useComposerStore()
		thread.value = { messages: [] }
		clearTopicName()
		setTopicType(null)
		composer.clearCurrentFile()
		clearErrorMessage()
	}

	// Прерывает ТОЛЬКО текстовый стрим текущего треда + чистит composer/loading/тред.
	// Задачи генерации живут в реестре по своему threadId и навигацией НЕ отменяются
	// (иначе теряется параллельная генерация) — их отменяет только generation.cancel(threadId).
	async function clearProccess() {
		const commonStore = useCommonStore()
		const composer = useComposerStore()
		fetchCounter = 0
		commonStore.resetLoading()
		commonStore.disableSendingMessage()
		cancelThreadStreamRequest()
		clearUserInteracted()
		setShouldStopStreaming()
		cancelThreadStream()
		clearThread()
		clearFetchThread()
		composer.clearCurrentMessage()
		clearAwaitMessage()
	}

	return {
		thread,
		threads,
		messageStream,
		reasoningMessageStream,
		currentTopicType,
		errorMessage,
		errorShowRetry,
		isStreaming,
		topicName,
		userInteracted,
		requestId,
		idMockUserMessage,
		shouldStopStreaming,
		fetchThread,
		prevThreadId,
		awaitMessage,
		deleteThreadArr,
		imgMode,
		imageEditMode,
		fileAvailable,
		secretMode,
		requestScrollBottom,
		threadName,
		withLoading,
		handleStreamEvent,
		processStream,
		createStreaming,
		stopStreaming,
		createMessageTemplate,
		createMockMessage,
		ensureUserPrompt,
		createCancelMessage,
		createStreamMessage,
		createThread,
		createMessage,
		applyThread,
		getThread,
		fetchThreadSilent,
		getThreadsList,
		deleteThread,
		sendAudio,
		cancelThreadStream,
		cancelThreadStreamRequest,
		addToDeleteThreadArr,
		setUserInteracted,
		setShouldStopStreaming,
		setTopicName,
		setErrorMessage,
		setFetchThread,
		toggleSecretChat,
		setSecretMode,
		setPrevThreadId,
		clearPevThreadId,
		clearSecretMode,
		clearErrorMessage,
		clearTopicName,
		clearFetchThread,
		clearUserInteracted,
		clearAwaitMessage,
		clearThread,
		clearProccess,
		setTopicType
	}
})
